import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { Activity, CheckCircle2, XCircle, AlertTriangle, Database, Cpu, HardDrive, Settings, Clock, RefreshCw, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { parseServerDate } from '../lib/constants';

const StatusPill = ({ ok, warn, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    ok ? 'bg-green-100 text-green-800' : warn ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
  }`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : warn ? <AlertTriangle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {children}
  </span>
);

const KV = ({ label, value, mono = false }) => (
  <div className="flex justify-between items-start gap-3 py-1.5 border-b border-border last:border-0">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-xs text-right ${mono ? 'font-mono' : 'font-medium'}`}>{value}</span>
  </div>
);

const SystemHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);

  const testAi = async () => {
    setAiTesting(true);
    setAiTestResult(null);
    try {
      const r = await api.post('/system/test-ai');
      setAiTestResult(r.data);
      if (r.data.ok) toast.success(`AI OK (${r.data.latency_ms} ms)`);
      else toast.error(`AI Error: ${(r.data.error || '').slice(0, 80)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal test AI');
    } finally {
      setAiTesting(false);
    }
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get('/system/health');
      setData(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal load health check');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return parseServerDate(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }); }
    catch (_) { return iso; }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="system-health-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Diagnostics</p>
            <h1 className="text-4xl font-medium tracking-tight flex items-center gap-3">
              <Activity className="w-8 h-8" /> System Health
            </h1>
            <p className="text-muted-foreground mt-1">Status MongoDB, AI provider, uploads, environment</p>
          </div>
          <Button onClick={load} disabled={refreshing} variant="outline" size="sm" data-testid="health-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading && (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        )}

        {data && (
          <>
            {/* Overall status */}
            <Card className={`border-2 ${data.healthy ? 'border-green-300 bg-green-50' : data.warnings.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}>
              <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {data.healthy ? <CheckCircle2 className="w-6 h-6 text-green-700" /> : <AlertTriangle className="w-6 h-6 text-amber-700" />}
                  <div>
                    <p className="font-semibold">
                      {data.healthy ? 'Semua sistem sehat' : `${data.warnings.length} peringatan`}
                    </p>
                    <p className="text-xs text-muted-foreground">Terakhir dicek {fmtDate(data.checked_at)}</p>
                  </div>
                </div>
                {data.warnings.length > 0 && (
                  <ul className="text-xs space-y-1 text-amber-900">
                    {data.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                  </ul>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* MongoDB */}
              <Card className="border-border" data-testid="health-mongo-card">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <Database className="w-4 h-4" /> MongoDB
                    </CardTitle>
                    <StatusPill ok={data.mongo.ok}>
                      {data.mongo.ok ? `${data.mongo.ping_ms} ms` : 'DOWN'}
                    </StatusPill>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {data.mongo.error && (
                    <p className="text-xs text-red-700 mb-2">Error: {data.mongo.error}</p>
                  )}
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Jumlah Dokumen per Collection</p>
                  {Object.entries(data.mongo.collections || {}).map(([k, v]) => (
                    <KV key={k} label={k} value={v == null ? '-' : v.toLocaleString('id-ID')} mono />
                  ))}
                </CardContent>
              </Card>

              {/* AI Provider */}
              <Card className="border-border" data-testid="health-ai-card">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <Cpu className="w-4 h-4" /> AI Provider
                    </CardTitle>
                    <StatusPill ok={data.ai.available} warn={!data.ai.available}>
                      {data.ai.available ? data.ai.active_provider : 'Belum aktif'}
                    </StatusPill>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <KV label="Provider Aktif" value={data.ai.active_provider || '-'} />
                  <KV label="Model" value={data.ai.active_model || '-'} mono />
                  <KV label="Google API Key" value={
                    <Badge className={`text-[10px] ${data.ai.google_key_configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {data.ai.google_key_configured ? 'Terpasang' : 'Kosong'}
                    </Badge>
                  } />
                  <KV label="Emergent Key" value={
                    <Badge className={`text-[10px] ${data.ai.emergent_key_configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {data.ai.emergent_key_configured ? 'Terpasang' : 'Kosong'}
                    </Badge>
                  } />
                  <KV label="Cache Entries" value={data.ai.cache_entries || 0} mono />
                  <KV label="Google SDK" value={data.ai.google_sdk_available ? '✓' : '✗'} />
                  <KV label="Emergent SDK" value={data.ai.emergent_sdk_available ? '✓' : '✗'} />
                  <div className="mt-3 pt-3 border-t border-border">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={testAi}
                      disabled={aiTesting || !data.ai.available}
                      className="w-full"
                      data-testid="test-ai-button"
                    >
                      <Zap className={`w-3.5 h-3.5 mr-2 ${aiTesting ? 'animate-pulse' : ''}`} />
                      {aiTesting ? 'Testing...' : 'Test AI Connection'}
                    </Button>
                    {aiTestResult && (
                      <div className={`mt-2 p-2 rounded text-xs ${aiTestResult.ok ? 'bg-green-50 border border-green-200 text-green-900' : 'bg-red-50 border border-red-200 text-red-900'}`}
                        data-testid="ai-test-result">
                        <p className="font-medium">
                          {aiTestResult.ok ? '✓' : '✗'} {aiTestResult.provider} • {aiTestResult.latency_ms} ms
                        </p>
                        {aiTestResult.response_sample && (
                          <p className="mt-1 font-mono text-[10px] break-all opacity-80">
                            {aiTestResult.response_sample.slice(0, 120)}
                          </p>
                        )}
                        {aiTestResult.error && (
                          <p className="mt-1 text-[10px] break-all">{aiTestResult.error}</p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Uploads / Disk */}
              <Card className="border-border" data-testid="health-uploads-card">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <HardDrive className="w-4 h-4" /> Uploads & Disk
                    </CardTitle>
                    <StatusPill
                      ok={(data.uploads.disk?.used_percent || 0) < 70}
                      warn={(data.uploads.disk?.used_percent || 0) >= 70 && (data.uploads.disk?.used_percent || 0) < 85}
                    >
                      {data.uploads.disk?.used_percent || 0}%
                    </StatusPill>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <KV label="Folder Uploads" value={data.uploads.exists ? '✓ Ada' : '✗ Tidak ditemukan'} />
                  <KV label="Total File" value={data.uploads.file_count.toLocaleString('id-ID')} mono />
                  <KV label="Ukuran Uploads" value={data.uploads.size_h} mono />
                  {data.uploads.disk?.total && (
                    <>
                      <KV label="Disk Total" value={data.uploads.disk.total_h} mono />
                      <KV label="Disk Digunakan" value={data.uploads.disk.used_h} mono />
                      <KV label="Disk Tersisa" value={data.uploads.disk.free_h} mono />
                      <div className="mt-3">
                        <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                          <div
                            className={`h-full ${data.uploads.disk.used_percent >= 85 ? 'bg-red-500' : data.uploads.disk.used_percent >= 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                            style={{ width: `${data.uploads.disk.used_percent}%` }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Environment */}
              <Card className="border-border" data-testid="health-env-card">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <Settings className="w-4 h-4" /> Environment
                    </CardTitle>
                    <StatusPill ok={['production', 'prod'].includes((data.environment.env || '').toLowerCase())}
                      warn={!['production', 'prod'].includes((data.environment.env || '').toLowerCase())}>
                      {data.environment.env || 'dev'}
                    </StatusPill>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <KV label="ENV" value={data.environment.env || 'dev'} mono />
                  <KV label="Admin Email" value={data.environment.admin_email || '-'} mono />
                  <KV label="DB Name" value={data.environment.db_name || '-'} mono />
                  <KV label="Python" value={data.environment.python} mono />
                  <KV label="CORS Origins" value={
                    <span className="text-[10px] font-mono break-all text-right">{data.environment.cors_origins || '-'}</span>
                  } />
                  <KV label="Frontend URL" value={
                    <span className="text-[10px] font-mono break-all text-right">{data.environment.frontend_url || '-'}</span>
                  } />
                  <KV label="Resend (email)" value={
                    <Badge className={`text-[10px] ${data.environment.resend_configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {data.environment.resend_configured ? 'Terpasang' : 'Kosong'}
                    </Badge>
                  } />
                  <KV label="Twilio (WhatsApp)" value={
                    <Badge className={`text-[10px] ${data.environment.twilio_configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {data.environment.twilio_configured ? 'Terpasang' : 'Kosong'}
                    </Badge>
                  } />
                </CardContent>
              </Card>
            </div>

            {/* Recent activity */}
            <Card className="border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Aktivitas Terakhir
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Tiket Terbaru</p>
                  <p className="font-medium text-sm">{data.activity.latest_ticket_number || '-'}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(data.activity.latest_ticket_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Login Terakhir</p>
                  <p className="font-medium text-sm">{data.activity.latest_login_email || '-'}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(data.activity.latest_login_at)}</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SystemHealthPage;
